import { useEffect, useState } from "react";
import { useTrelloPowerUpContext } from "../../../../external/trello-pu__core/trello-iframe/context";
// import useTrelloRestApi from "../../modal/hooks/use-trello-rest-api";
// import trelloIFrame from "../trello-client/trello-iframe";

type TSingleBoardTargetSO = {
  board: string;
  done: string[];
  wip: string[];
};
export type TBoardsTargetsSO = ReadonlyArray<TSingleBoardTargetSO>;

const useBoardTargets = () => {
  const { trelloIFrame, restApi } = useTrelloPowerUpContext();
  const [boardTargets, setBoardTargets] = useState<TBoardTarget[]>([]);

  useEffect(() => {
    refresh(setBoardTargets);
  }, []);

  function addTarget(value: TBoardTarget) {
    const newState = [...boardTargets].filter(
      (t) => t.board.id != value.board.id
    );
    newState.push(value);
    setBoardTargetsInCardData(newState).then(() => setBoardTargets(newState));
  }

  function removeTarget(idBoard: string) {
    const newState = [...boardTargets].filter((t) => t.board.id != idBoard);
    trelloIFrame
      .set("card", "shared", "boardTargets", mapBoardTargetToSO(newState))
      .then(() => setBoardTargets(newState));
  }

  function refresh(
    callback: (targets: TBoardTarget[]) => void
  ): PromiseLike<TBoardTarget[]> {
    return trelloIFrame
      .get("card", "shared", "boardTargets")
      .then(mapSOToBoardTarget)
      .then((targets) => {
        if (targets.idBoardsRemoved.length > 0) {
          //update targets on card data if given board has not been found
          setBoardTargetsInCardData(targets.validTargets);
        }
        callback(targets.validTargets);
        return targets.validTargets;
      });
  }

  function setBoardTargetsInCardData(newState: TBoardTarget[]) {
    return trelloIFrame.set(
      "card",
      "shared",
      "boardTargets",
      mapBoardTargetToSO(newState)
    );
  }

  function mapBoardTargetToSO(targets: TBoardTarget[]): TBoardsTargetsSO {
    const so: TBoardsTargetsSO = targets.map((t) => ({
      board: t.board.id,
      done: t.listsDone.map((l) => l.id),
      wip: t.listsWIP.map((l) => l.id),
    }));
    return so;
  }

  async function mapSOToBoardTarget(
    targets?: TBoardsTargetsSO
  ): Promise<{ validTargets: TBoardTarget[]; idBoardsRemoved: string[] }> {
    const idBoardsRemoved: string[] = [];
    const validTargets = await Promise.all(
      targets?.map(async (t) => {
        const board = await restApi.fetchBoard(t.board, "open").catch(() => {
          idBoardsRemoved.push(t.board);
          return null;
        });

        if (!board) {
          return null;
        } else {
          return {
            board: { id: t.board, name: board.name },
            listsDone: board.lists
              ?.filter((l) => t.done.some((d) => d == l.id))
              .map((l) => ({ id: l.id, name: l.name })) as TIdNamePair[],
            listsWIP: board.lists
              ?.filter((l) => t.wip.some((d) => d == l.id))
              .map((l) => ({ id: l.id, name: l.name })) as TIdNamePair[],
          };
        }
      }) ?? []
    ).then((targets) => targets.filter((x): x is TBoardTarget => x !== null));
    return { validTargets, idBoardsRemoved };
  }

  const refreshTargets = () => refresh(setBoardTargets);

  return [boardTargets, addTarget, removeTarget, refreshTargets] as const;
};

export default useBoardTargets;
